import { Livechat } from "@api";

import {
  genInternalMessage,
  messageTransformer,
  replacePostback,
  transformMessage,
} from "@libs";
import {
  EMessageStatus,
  EMessageType,
  ESender,
  ICombinedMessage,
  IInCommingMessage,
  IOutGoingPostbackMessage,
  IOutGoingResponse,
  IRoom,
  IUser,
} from "@types";
import { IOutGoingMessage } from "@types";
import dayjs from "dayjs";
import { assign, createMachine } from "xstate";

export type IChatMachineBoxContext = {
  firstItemIndex: number;
  messages: ICombinedMessage[];
};

export const chatMachine = (room: IRoom, user: IUser) =>
  createMachine<IChatMachineBoxContext>(
    {
      id: "chatMachine",
      initial: "firstFetchingMessages",
      context: {
        firstItemIndex: 20000,
        messages: [] as ICombinedMessage[],
      },
      invoke: [
        {
          id: "sendMessageService",
          src: "sendMessage",
        },
        {
          id: "uploadAttachmentService",
          src: "uploadFile",
        },
      ],
      states: {
        idle: {},
        firstFetchingMessages: {
          invoke: {
            id: "fetchMessages",
            src: "fetchMessages",
            onDone: {
              target: "idle",
              actions: "updateMessages",
            },
            onError: {
              target: "firstFetchMessageFailed",
            },
          },
        },
        firstFetchMessageFailed: {},
        sendingMessage: {},
        uploadingMessage: {},
        sendMessageSuccess: {},
        sendMessageFail: {},
        fetchingMoreMessages: {
          invoke: {
            id: "fetchMoreMessages",
            src: "fetchMessages",
            onDone: {
              target: "idle",
              actions: ["prependMessages", "updateFirstIndexItem"],
            },
            onError: {
              target: "fetchMoreMessagesFailed",
            },
          },
        },
        fetchMoreMessagesFailed: {},
      },
      on: {
        SEND_MESSAGE: {
          target: "sendingMessage",
          actions: ["addMessage", "sendMessage"],
        },
        NEW_INCOMING_MESSAGE: {
          actions: ["appendIncomingMessage"],
        },
        SEND_MESSAGE_SUCCESS: {
          target: "idle",
          actions: "updateMessageStatus",
        },
        SEND_MESSAGE_FAILED: {
          target: "idle",
          actions: "updateMessageStatus",
        },

        UPLOAD_ATTACHMENT: {
          target: "uploadingMessage",
          actions: ["addMessage", "uploadFile"],
        },
        UPLOAD_ATTACHMENT_SUCCESS: {
          target: "idle",
          actions: "updateMessageStatus",
        },
        UPLOAD_ATTACHMENT_FAILED: {
          target: "idle",
          actions: "updateMessageStatus",
        },
        LOAD_MORE_MESSAGES: {
          target: "fetchingMoreMessages",
        },
      },
    },
    {
      actions: {
        addMessage: assign({
          messages: (context, event) => {
            const payload = event.payload;
            const msgType = event.msgType as EMessageType;
            const { messages } = context;
            // postback message
            if (msgType === EMessageType.POSTBACK) {
              const message = genInternalMessage(
                EMessageType.TEXT,
                EMessageStatus.SENDING,
                {
                  ...payload,
                  msg: payload.displayMsg,
                },
                dayjs().toISOString()
              );

              if (!message) return messages;
              messages.push(message);
              return messages;
            }
            // text message
            const message = genInternalMessage(
              msgType,
              EMessageStatus.SENDING,
              payload,
              dayjs().toISOString()
            );
            if (!message) return messages;
            messages.push(message);

            return messages;
          },
        }),
        appendIncomingMessage: assign({
          messages: (context, event) => {
            const { uid, newMessage } = event.payload as {
              uid: string;
              newMessage: IInCommingMessage;
            };
            // window active
            const { messages } = context;
            if (document.hasFocus()) {
              const sender =
                uid === newMessage.u._id ? ESender.USER : ESender.BOT;
              if (sender === ESender.BOT) {
                const message = transformMessage(
                  uid,
                  newMessage
                ) as ICombinedMessage;
                if (message) {
                  messages.push(message);
                }
                return messages;
              }
              return messages;
            } else {
              // find and update message
              const index = messages.findIndex((message) => {
                return message.id === newMessage._id;
              });

              if (index !== -1) {
                messages[index] = transformMessage(
                  uid,
                  newMessage
                ) as ICombinedMessage;
                return messages;
              }

              const message = transformMessage(
                uid,
                newMessage
              ) as ICombinedMessage;

              if (message) {
                messages.push(message);
              }
              return messages;
            }
          },
        }),
        updateMessages: assign({
          messages: (context, event) => {
            const payload = event.data as ICombinedMessage[];
            const { messages } = context;
            messages.push(...payload);
            return messages;
          },
        }),
        updateMessageStatus: assign({
          messages: (context, event) => {
            const payload = event.data as ICombinedMessage;
            const { messages } = context;
            const index = messages.findIndex(
              (message) => message.id === payload.id
            );
            if (index !== -1) {
              messages[index] = {
                ...payload,
              };
            }
            return messages;
          },
        }),
        prependMessages: assign({
          messages: (context, event) => {
            const payload = event.data as ICombinedMessage[];
            const { messages } = context;
            const newMessage = [...payload, ...messages];
            return newMessage;
          },
        }),
        updateFirstIndexItem: assign({
          firstItemIndex: (context, event) => {
            const payload = event.data as ICombinedMessage[];
            const { firstItemIndex } = context;
            return firstItemIndex - payload.length;
          },
        }),
      },
      services: {
        fetchMessages: async (e, event) => {
          const msg = await Livechat.loadMessages(room._id, {
            token: user.token,
            limit: 1000,
          });
          const transformMessage = messageTransformer(user._id, msg);
          return transformMessage.reverse();
        },
        uploadFile: (_, event) => async (cb) => {
          try {
            if (event.type === "UPLOAD_ATTACHMENT") {
              const file = event.file as File | null;
              if (file) {
                // Handle the selected files here
                const selectedFile = file;
                // upload file
                const result: IInCommingMessage = await Livechat.uploadFile({
                  rid: room._id,
                  file: selectedFile,
                });
                const message: ICombinedMessage | null = genInternalMessage(
                  event.msgType,
                  EMessageStatus.SENT,
                  event.payload,
                  result.ts
                );

                cb({
                  type: "UPLOAD_ATTACHMENT_SUCCESS",
                  data: message,
                });
              }
            }
          } catch (error) {
            console.error("error while upload file", error);

            const message: ICombinedMessage | null = genInternalMessage(
              event.msgType,
              EMessageStatus.ERROR,
              event.payload,
              Date.now().toString()
            );
            cb({
              type: "UPLOAD_ATTACHMENT_FAILED",
              data: message,
            });
          }
        },
        sendMessage: (_, event) => async (cb, onReceive) => {
          switch (event.type) {
            case "SEND_MESSAGE":
              const payload = event.payload as IOutGoingMessage;
              try {
                // transform just key that required for send
                const outMessage: IOutGoingMessage = {
                  _id: payload._id,
                  msg: payload.msg,
                  token: payload.token,
                  rid: payload.rid,
                };
                const result: IOutGoingResponse = await Livechat.sendMessage(
                  outMessage
                );
                const message: ICombinedMessage = {
                  id: result.message._id,
                  type: EMessageType.TEXT,
                  sender: ESender.USER,
                  text: replacePostback(result.message.msg),
                  status: EMessageStatus.SENT,
                  createdAt: result.message.ts,
                };
                cb({
                  type: "SEND_MESSAGE_SUCCESS",
                  data: message,
                });
              } catch (e) {
                console.error("error send message", e);
                const message: ICombinedMessage = {
                  id: payload._id,
                  type: EMessageType.TEXT,
                  sender: ESender.USER,
                  text: payload.msg,
                  status: EMessageStatus.ERROR,
                  createdAt: dayjs().toISOString(),
                };
                cb({
                  type: "SEND_MESSAGE_FAILED",
                  data: message,
                });
              }
          }
        },
      },
    }
  );
